import React, { memo, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { SortArrowToggle } from 'transfix-ui/components/SortArrowToggle'
import { ICarrierState } from 'types/carrier'
import { IShipmentsState } from 'types/shipment'
import { getShipments } from 'utils/actions/shipments'
import { IStore } from 'apps/carrier/utils/reducers'
import { Pagination, Spinner } from 'transfix-ui'
import { DEFAULT_SHIPMENT_FILTERS, DEFAULT_PER_PAGE } from '../../constants/carrierProfile'
import ShipmentRow from './ShipmentRow'

interface ISelector {
  shipments: IShipmentsState
  carrier: ICarrierState
}

export interface IShipmentsProps {
  shipmentsCount: number
}

const Shipments = memo<IShipmentsProps>(({ shipmentsCount }) => {
  const {
    carrier: { carrierDetails },
    shipments: { error, firstLoad, isFetching, shipmentList },
  } = useSelector<IStore, ISelector>(({ carrier, shipments }) => ({ shipments, carrier }))

  const [pageCount, setPageCount] = useState<number>(0)
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc')
  const dispatch = useDispatch()
  useEffect(() => {
    const carrierId = carrierDetails?.id
    const shipmentParams = {
      ...DEFAULT_SHIPMENT_FILTERS,
      filters: {
        usersId: carrierId,
        ...DEFAULT_SHIPMENT_FILTERS.filters,
      },
      sortOrder,
      pageCount,
    }
    dispatch(getShipments(shipmentParams))
  }, [dispatch, carrierDetails?.id, pageCount, sortOrder])
  const onPageChange = (pageNumber: number) => {
    setPageCount(pageNumber - 1)
  }
  const onSetSortOrder = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    setPageCount(0)
  }
  const refreshPage = () => {
    global.location.reload()
  }

  let renderElement
  if (isFetching && firstLoad) {
    renderElement = (
      <div className='pa6'>
        <Spinner position='relative' />
      </div>
    )
  } else if (!firstLoad && !isFetching && shipmentList.length === 0 && !error) {
    renderElement = (
      <div className='tc'>
        <div className='pa6'>This carrier hasn’t been offered any loads yet.</div>
      </div>
    )
  } else {
    renderElement = (
      <>
        <table className='table'>
          <thead>
            <tr>
              <th>Shipment ID</th>
              <th>Equipment</th>
              <th>
                <button className='sort' onClick={onSetSortOrder}>
                  Pickup Date <SortArrowToggle direction={sortOrder} />
                </button>
              </th>
              <th>Pickup City</th>
              <th>Delivery City</th>
              <th>Shipper Rate</th>
              <th>Carrier Rate</th>
              <th>Shipper</th>
              <th>Status</th>
              <th>Driver</th>
            </tr>
          </thead>
          <tbody data-test='shipment-list'>
            {error && (
              <tr>
                <td colSpan={9} className='tc'>
                  <div className='tc'>
                    <div className='pa6'>
                      <div className='pv4'>
                        Sorry, there was an issue loading results. Please try refreshing your page.
                      </div>
                      <button className='btn-text-link' onClick={refreshPage}>
                        Refresh your page
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            )}
            {isFetching && (
              <tr>
                <td aria-label='Loading shipments' colSpan={9} className='tc'>
                  <div className='tc'>
                    <div className='pa6'>
                      <Spinner position='relative' />
                    </div>
                  </div>
                </td>
              </tr>
            )}
            {!isFetching &&
              shipmentList.length > 0 &&
              shipmentList.map(shipment => <ShipmentRow shipment={shipment} key={shipment.id} />)}
          </tbody>
        </table>
        <div className='tc'>
          <Pagination
            currentPage={pageCount + 1}
            numberOfPages={Math.ceil(shipmentsCount / DEFAULT_PER_PAGE)}
            onPageChange={onPageChange}
          />
        </div>
      </>
    )
  }
  return (
    <div data-test='shipments-tab-content'>
      <div className='row'>
        <div className='col-md-12'>{renderElement}</div>
      </div>
    </div>
  )
})

export default Shipments
